import { useCallback, useMemo, useState } from 'react';
import { Tag } from 'antd';
import { formatNumber } from 'shared/utils';
import { colorPalette } from 'shared/constants';
import { MAX_COMPARED_USERS, TREND_STATUS } from 'shared/constants/quality-metric.constants';
import {
  calculateTrendDelta,
  displayPercentage,
  isSuccess,
} from 'shared/utils/quality-metrics.utils';
import { useMetricUsers } from 'shared/hooks/quality-metric.hooks';
import {
  AG_FILTER_TYPES,
  RadioSetFilter,
  SetFilter,
  Table,
  cellActionKeyboardNavigate,
  radioSetFilterDoesFilterPass,
  setFilterDoesFilterPass,
} from 'src/components/design-system/table';
import { Button, SectionTitle } from 'src/components/design-system';
import { Loader } from 'src/components/core';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import { UserCasesModal } from './user-cases-modal';
import styles from '../quality-metrics-management.module.scss';

// The endpoint's maximum. The column filters are client-side (the endpoint
// takes no filter params), so pulling the largest page keeps them meaningful
// instead of only covering 50 rows at a time. The grid paginates the result —
// `autoHeight` turns off row virtualisation, so without that every fetched row
// would sit in the DOM at once.
const PAGE_SIZE = 500;
const GRID_PAGE_SIZE = 25;
const GRID_PAGE_SIZE_OPTIONS = [25, 50, 100];

// `selectAll: false` hides the header checkbox: selecting every row would blow
// past the comparison cap on the first click.
const ROW_SELECTION = { mode: 'multiRow', checkboxes: true, selectAll: false };

const TREND_PREFIX = {
  [TREND_STATUS.UP]: '+',
  [TREND_STATUS.DOWN]: '',
  [TREND_STATUS.NO_CHANGE]: '',
};

/** The label the trend column filters and sorts on. */
const trendLabelOf = (trend, lang, metricLang) => {
  switch (trend?.status) {
    case TREND_STATUS.UP:
      return lang.TREND_UP;
    case TREND_STATUS.DOWN:
      return lang.TREND_DOWN;
    case TREND_STATUS.NO_CHANGE:
      return metricLang.TREND_NO_CHANGE;
    default:
      return lang.NOT_ENOUGH_DATA;
  }
};

/**
 * The shared `TrendIndicator` is styled white for the dark metric card, so it
 * is invisible in a grid row — this renders the same delta for a light one.
 */
function TrendCell({ trend, naLabel }) {
  if (!trend || trend.status === TREND_STATUS.NA) {
    return <span className={styles.muted}>{naLabel}</span>;
  }
  return (
    <span
      style={{
        color:
          trend.status === TREND_STATUS.DOWN
            ? colorPalette.redNegative
            : colorPalette.greenPositive,
      }}>
      {TREND_PREFIX[trend.status]}
      {trend.delta}%
    </span>
  );
}

/**
 * Per-user standing on the selected metric, from `GET /:id/users` (Root only).
 *
 * Status, records and trend filter through the design-system grid filters, so
 * they get the shared chips, keyboard handling and clear-all for free.
 *
 * The endpoint's own `rate` is ignored in favour of `displayPercentage`, so an
 * inverted metric reads the same direction here as everywhere else on the page.
 */
export function ManagementUsersTable({ selectedUserIds = [], onSelectedUserIdsChange }) {
  const { selectedMetric, lang, metricLang, usersFilterModel, setUsersFilterModel } =
    useQualityMetricsManagementContext();
  const [page, setPage] = useState(1);
  const [casesUser, setCasesUser] = useState(null);

  const metricId = selectedMetric?.id;
  const { data, isLoading, isFetching } = useMetricUsers(metricId, { page, pageSize: PAGE_SIZE });

  const invert = selectedMetric?.invert;
  const groupGoal = selectedMetric?.groupGoal;

  // Derived once per user so the filters and the cell renderers read the same
  // values, rather than each recomputing the delta.
  const rows = useMemo(
    () =>
      (data?.users ?? []).map(user => ({
        ...user,
        trend: calculateTrendDelta({ trends: user.trends, invert }),
        compliant: user.totalRecords
          ? isSuccess({ total: user.totalRecords, success: user.totalSuccess, groupGoal })
          : null,
      })),
    [data?.users, invert, groupGoal],
  );

  // Replay the URL state onto a freshly mounted grid. `onGridReady` fires once
  // per mount, and the table remounts per metric, so this is the only place the
  // two can be reconciled without fighting the user's later edits.
  const handleGridReady = useCallback(
    event => {
      if (usersFilterModel) event.api.setFilterModel(usersFilterModel);
      if (selectedUserIds.length) {
        event.api.forEachNode(node => {
          if (selectedUserIds.includes(node.data?.userId)) node.setSelected(true);
        });
      }
    },
    // Deliberately not reactive: this restores the state the grid mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleFilterChanged = useCallback(
    event => setUsersFilterModel(event.api.getFilterModel()),
    [setUsersFilterModel],
  );

  // AG Grid has no built-in selection cap, so the overflow is deselected as it
  // happens. Doing it here re-fires `selectionChanged`, and that second pass
  // reports the capped set — hence the early return.
  const handleSelectionChanged = useCallback(
    event => {
      const nodes = event.api.getSelectedNodes();
      if (nodes.length > MAX_COMPARED_USERS) {
        nodes.slice(MAX_COMPARED_USERS).forEach(node => node.setSelected(false));
        return;
      }
      onSelectedUserIdsChange?.(nodes.map(node => node.data.userId));
    },
    [onSelectedUserIdsChange],
  );

  const statusFilterOptions = useMemo(
    () => [
      {
        value: metricLang.COMPLIANT,
        label: metricLang.COMPLIANT,
        matcher: v => v === metricLang.COMPLIANT,
      },
      {
        value: metricLang.NON_COMPLIANT,
        label: metricLang.NON_COMPLIANT,
        matcher: v => v === metricLang.NON_COMPLIANT,
      },
    ],
    [metricLang],
  );

  const trendFilterOptions = useMemo(
    () => [
      { text: lang.TREND_UP, value: lang.TREND_UP },
      { text: lang.TREND_DOWN, value: lang.TREND_DOWN },
      { text: metricLang.TREND_NO_CHANGE, value: metricLang.TREND_NO_CHANGE },
      { text: lang.NOT_ENOUGH_DATA, value: lang.NOT_ENOUGH_DATA },
    ],
    [lang, metricLang],
  );

  const columns = useMemo(
    /** @type {() => import('ag-grid-community').ColDef[]} */
    () => [
      {
        headerName: lang.COLUMN_NAME,
        colId: 'name',
        flex: 2,
        filter: AG_FILTER_TYPES.TEXT,
        valueGetter: ({ data: row }) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
      },
      { headerName: lang.COLUMN_EMAIL, field: 'email', flex: 2, filter: AG_FILTER_TYPES.TEXT },
      {
        headerName: lang.COLUMN_RATE,
        colId: 'status',
        flex: 1,
        // The filter reads this label; the renderer draws the percentage + tag.
        valueGetter: ({ data: row }) => {
          if (row.compliant === null) return null;
          return row.compliant ? metricLang.COMPLIANT : metricLang.NON_COMPLIANT;
        },
        cellRenderer: ({ data: row }) => {
          if (!row.totalRecords) return <span className={styles.muted}>{metricLang.NO_DATA}</span>;
          return (
            <span className={styles.rateCell}>
              {displayPercentage({ total: row.totalRecords, success: row.totalSuccess, invert })}
              <Tag color={row.compliant ? 'green' : 'red'} bordered={false}>
                {row.compliant ? metricLang.COMPLIANT : metricLang.NON_COMPLIANT}
              </Tag>
            </span>
          );
        },
        filter: { component: RadioSetFilter, doesFilterPass: radioSetFilterDoesFilterPass },
        filterParams: { options: statusFilterOptions },
      },
      {
        headerName: lang.COLUMN_RECORDS,
        field: 'totalRecords',
        flex: 1,
        filter: AG_FILTER_TYPES.NUMBER,
        cellRenderer: ({ data: row }) => formatNumber(row.totalRecords),
      },
      {
        headerName: lang.COLUMN_TREND,
        colId: 'trend',
        flex: 1,
        // Sort on the signed delta so "most improved" and "worst decline" land
        // at the two ends. The cell value is the label the filter matches on,
        // so the delta has to come off the row node rather than the value.
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data.trend?.delta ?? 0) - (nodeB.data.trend?.delta ?? 0),
        valueGetter: ({ data: row }) => trendLabelOf(row.trend, lang, metricLang),
        cellRenderer: ({ data: row }) => (
          <TrendCell trend={row.trend} naLabel={lang.NOT_ENOUGH_DATA} />
        ),
        filter: { component: SetFilter, doesFilterPass: setFilterDoesFilterPass },
        filterParams: { options: trendFilterOptions },
      },
      {
        headerName: lang.COLUMN_MEETS_MINIMUM,
        field: 'meetsMinimumRecords',
        flex: 1,
        cellRenderer: ({ data: row }) =>
          row.meetsMinimumRecords ? (
            lang.YES
          ) : (
            <span className={styles.muted}>
              {lang.NO} ({formatNumber(row.totalRecords)}/{formatNumber(row.minimumRecords)})
            </span>
          ),
      },
      {
        headerName: lang.COLUMN_ACTIONS,
        colId: 'actions',
        flex: 1,
        sortable: false,
        cellRenderer: ({ data: row }) => (
          <Button
            type="link"
            disabled={!row.totalRecords}
            onClick={() => setCasesUser(row)}
            aria-label={lang.VIEW_CASES_FOR.replace(
              '{name}',
              `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || row.email,
            )}>
            {lang.VIEW_CASES}
          </Button>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
    ],
    [lang, metricLang, invert, statusFilterOptions, trendFilterOptions],
  );

  if (isLoading) {
    return <Loader />;
  }

  const totalPages = data?.totalPages ?? 1;

  return (
    <section className={styles.usersSection}>
      <SectionTitle title={lang.USERS_TITLE} />

      {rows.length === 0 ? (
        <p className={styles.emptyText}>{lang.NO_USERS}</p>
      ) : (
        <>
          {/* Two sentences, two elements: joining them with a literal separator
              would make the punctuation and word order untranslatable. */}
          <p className={styles.compareHint}>
            <span>{lang.COMPARE_HINT.replace('{max}', formatNumber(MAX_COMPARED_USERS))}</span>
            {selectedUserIds.length > 0 && (
              <span>
                {lang.COMPARE_SELECTED.replace(
                  '{count}',
                  formatNumber(selectedUserIds.length),
                ).replace('{max}', formatNumber(MAX_COMPARED_USERS))}
              </span>
            )}
          </p>

          <Table
            containerProps={{ className: styles.usersTable }}
            columnDefs={columns}
            rowData={rows}
            getRowId={params => params.data.userId}
            domLayout="autoHeight"
            pagination
            paginationPageSize={GRID_PAGE_SIZE}
            paginationPageSizeSelector={GRID_PAGE_SIZE_OPTIONS}
            enableColumnChooser
            enableActiveFilters
            rowSelection={ROW_SELECTION}
            onGridReady={handleGridReady}
            onFilterChanged={handleFilterChanged}
            onSelectionChanged={handleSelectionChanged}
          />

          {totalPages > 1 && (
            <div className={styles.pager}>
              {/* Column filters only reach the loaded page — say so rather than
                  letting a filtered view look like the whole organization. */}
              <span className={styles.muted}>{lang.FILTERS_PAGE_ONLY}</span>
              <Button
                type="default"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage(current => current - 1)}>
                {lang.PREVIOUS}
              </Button>
              <span className={styles.muted}>
                {lang.PAGE_OF.replace('{page}', formatNumber(data?.page ?? page)).replace(
                  '{total}',
                  formatNumber(totalPages),
                )}
              </span>
              <Button
                type="default"
                disabled={!data?.hasMore || isFetching}
                onClick={() => setPage(current => current + 1)}>
                {lang.NEXT}
              </Button>
            </div>
          )}
        </>
      )}

      <UserCasesModal open={!!casesUser} user={casesUser} onClose={() => setCasesUser(null)} />
    </section>
  );
}
